import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ContractsPolicy } from '../../contracts/ContractsPolicy.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from '../../permissions/abac-payloads/checkLoggedInPayload.js';
import { MARTINUS_ABAC_PAYLOAD } from '../../permissions/abac-payloads/MartinusAbacPayload.js';
import { usersHaveSameSchoolOnRoles } from '../../permissions/api/getSchoolIDsForRoles.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { isSelf } from '../../permissions/api/isSelf.js';
import { EMPLOYEE_ROLES, Role } from '../../permissions/roles.js';
import type { UserWithPermissionFields } from '../../permissions/UserWithPermissionFields.js';
import type { UserOnUserPayload } from '../UserPolicy.js';
import type { Abrechnungsgruppe } from './entities/employee-sections/an-beschaeftigung.js';

/**
 * The employee pages need a few more fields to be compliant with the permissions system.
 * This interface extends the UserWithPermissionFields with the additional fields needed.
 */
export interface EmployeeDetailsUser extends UserWithPermissionFields {
  mitarbeiterDaten: Maybe<{ anBeschaeftigung: Maybe<{ abrechnungsgruppe: Maybe<Abrechnungsgruppe> }> }>;
  hasLAK?: Maybe<boolean>;
}

export enum UserEmployeeDetailsActions {
  /**
   * Can view the employee tab
   */
  VIEW_EMPLOYEE_TAB = 'VIEW_EMPLOYEE_TAB',

  /**
   * Can view the employee section details
   */
  VIEW_EMPLOYEE_SECTION_DETAILS = 'VIEW_EMPLOYEE_SECTION_DETAILS',

  /**
   * Can view the employee section details
   */
  VIEW_SECTION_EMPLOYEE_DETAILS = 'VIEW_SECTION_EMPLOYEE_DETAILS',

  /**
   * Can edit the employee section details
   */
  EDIT_EMPLOYEE_SECTION_DETAILS = 'EDIT_EMPLOYEE_SECTION_DETAILS',

  /**
   * Can view the ticket tab
   */
  VIEW_TICKET_TAB = 'VIEW_TICKET_TAB',

  /**
   * View contracts
   * @deprecated use `ContractsActions.VIEW_CONTRACTS`. Kept so existing callers keep working, it delegates to
   * the contracts policy.
   */
  VIEW_CONTRACTS = 'VIEW_CONTRACTS',

  /**
   * Can view the LAK tab
   */
  VIEW_LAK = 'VIEW_LAK',

  /**
   * Can maintain the LAK hours which were accumulated before this system calculated them
   */
  EDIT_LAK = 'EDIT_LAK',

  /**
   * Can view the Beschäftigungsgenehmigung tab
   */
  VIEW_BESCHAEFTIGUNGSGENEHMIGUNG = 'VIEW_BESCHAEFTIGUNGSGENEHMIGUNG',

  /**
   * View the persons calender
   * TODO move this into an own calender policy
   */
  VIEW_KALENDER = 'VIEW_KALENDER',

  /**
   * Can view recruitment documents
   */
  VIEW_RECRUITMENT_DOCUMENTS = 'VIEW_RECRUITMENT_DOCUMENTS',

  /**
   * Can view compliance documents
   */
  VIEW_COMPLIANCE_DOCUMENTS = 'VIEW_COMPLIANCE_DOCUMENTS',

  /**
   * Can view the internally saved employment documents, like the signed contract and so on
   */
  VIEW_INTERNAL_EMPLOYMENT_DOCUMENTS = 'VIEW_INTERNAL_EMPLOYMENT_DOCUMENTS',

  /**
   * View Führungszeugnis.
   */
  VIEW_FUEHRUNGSZEUGNIS = 'VIEW_FUEHRUNGSZEUGNIS',

  /**
   * Can view the employee info section details
   */
  VIEW_EMPLOYEE_SECTION_INFOS = 'VIEW_EMPLOYEE_SECTION_INFOS',

  /**
   * Can view the employee tax section details
   */
  VIEW_EMPLOYEE_SECTION_STEUERINFOS = 'VIEW_EMPLOYEE_SECTION_STEUERINFOS',

  /**
   * Can view the employee social insurance section details
   */
  VIEW_EMPLOYEE_SECTION_SOZIALVERSICHERUNG = 'VIEW_EMPLOYEE_SECTION_SOZIALVERSICHERUNG',

  /**
   * Can view the employee education section details
   */
  VIEW_EMPLOYEE_SECTION_AUSBILDUNG = 'VIEW_EMPLOYEE_SECTION_AUSBILDUNG',

  /**
   * Can view the employee private insurance section details
   */
  VIEW_EMPLOYEE_SECTION_PRIVATVERSICHERUNG = 'VIEW_EMPLOYEE_SECTION_PRIVATVERSICHERUNG',

  /**
   * Can view the employee marginal employment section details
   */
  VIEW_EMPLOYEE_SECTION_GERINGFUEGIGE_BESCHAEFTIGUNG = 'VIEW_EMPLOYEE_SECTION_GERINGFUEGIGE_BESCHAEFTIGUNG',

  /**
   * Can view the employee disability section details
   */
  VIEW_EMPLOYEE_SECTION_SCHWERBEHINDERUNG = 'VIEW_EMPLOYEE_SECTION_SCHWERBEHINDERUNG',

  /**
   * Can view the employee capital-forming benefits section details
   */
  VIEW_EMPLOYEE_SECTION_VERMOEGENSWIRKSAME_LEISTUNGEN = 'VIEW_EMPLOYEE_SECTION_VERMOEGENSWIRKSAME_LEISTUNGEN',

  /**
   * Can view the employee documents section details
   */
  VIEW_EMPLOYEE_SECTION_DOKUMENTE = 'VIEW_EMPLOYEE_SECTION_DOKUMENTE',

  /**
   * Can view the employee personnel file section details
   */
  VIEW_EMPLOYEE_SECTION_PERSONALAKTE = 'VIEW_EMPLOYEE_SECTION_PERSONALAKTE',

  /**
   * Can view the employee employment section details
   */
  VIEW_EMPLOYEE_SECTION_BESCHAEFTIGUNG = 'VIEW_EMPLOYEE_SECTION_BESCHAEFTIGUNG',

  /**
   * Can view the employee consent declarations section details
   */
  VIEW_EMPLOYEE_SECTION_EINVERSTAENDNISERKLAERUNGEN = 'VIEW_EMPLOYEE_SECTION_EINVERSTAENDNISERKLAERUNGEN',

  /**
   * Can view the employee working student section details
   */
  VIEW_EMPLOYEE_SECTION_WORKING_STUDENT = 'VIEW_EMPLOYEE_SECTION_WORKING_STUDENT',
}

const UserEmployeeDetailsPolicyFunctions: Record<
  UserEmployeeDetailsActions,
  LoggedInEvaluationFunction<UserOnUserPayload & { viewedUser: EmployeeDetailsUser }>
> = {
  /**
   * All internal employeement administrative personal can view this tap
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_TAB]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB, Role.MAV];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * All internal employeement administrative personal can view this tap
   */
  [UserEmployeeDetailsActions.EDIT_EMPLOYEE_SECTION_DETAILS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can edit the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten editieren. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * View Employee Section details can by default be seen by everyone who can see the employee tab
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_DETAILS]: async request => {
    return UserEmployeeDetailsPolicyFunctions[UserEmployeeDetailsActions.VIEW_EMPLOYEE_TAB](request);
  },

  /**
   * View Employee Section details can by default be seen by everyone who can see the employee tab
   */
  [UserEmployeeDetailsActions.VIEW_SECTION_EMPLOYEE_DETAILS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_INFOS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_STEUERINFOS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_SOZIALVERSICHERUNG]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_AUSBILDUNG]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_PRIVATVERSICHERUNG]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_WORKING_STUDENT]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_GERINGFUEGIGE_BESCHAEFTIGUNG]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_SCHWERBEHINDERUNG]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_VERMOEGENSWIRKSAME_LEISTUNGEN]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_DOKUMENTE]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB, Role.MAV];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_PERSONALAKTE]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_BESCHAEFTIGUNG]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * Temporarily delegates to the generic employee section permission.
   */
  [UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_EINVERSTAENDNISERKLAERUNGEN]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });

    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer ist nicht an der gleichen Schule.' };
  },

  /**
   * The ticket can only be viewed by the psb and schulleitung for the persons and also if the person himself
   */
  [UserEmployeeDetailsActions.VIEW_TICKET_TAB]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Ticket-Tab sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für den Ticket-Tab.' };
  },

  /**
   * @deprecated the contracts own their access rules now, this only forwards so existing callers keep working.
   */
  [UserEmployeeDetailsActions.VIEW_CONTRACTS]: async request => {
    return ContractsPolicy.evaluate(request as Parameters<typeof ContractsPolicy.evaluate>[0]);
  },

  /**
   * The user can see its own contracts. The administrative roles like psb and schulleitung can view the contracts.
   */
  [UserEmployeeDetailsActions.VIEW_LAK]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf das LAK sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für das LAK.' };
  },

  /**
   * The hours accumulated before this system calculated the LAK are a manual correction of the whole
   * calculation, so only the PSB maintains them. As long as the LAK itself is experimental this follows
   * the same restriction as VIEW_LAK.
   */
  [UserEmployeeDetailsActions.EDIT_LAK]: async request => {
    if (hasRoles({ user: request.payload.currentUser, roles: [Role.PSB], contexts: [GLOBAL_CONTEXT] })) {
      return { allowed: true, reason: 'Nutzer ist PSB und darf die übernommenen LAK-Stunden pflegen.' };
    }

    return { allowed: false, reason: 'Nur die Personalsachbearbeitung darf die übernommenen LAK-Stunden pflegen.' };
  },

  /**
   * Only administrative personal for the employees can view the Beschäftigungsgenehmigung tab
   */
  [UserEmployeeDetailsActions.VIEW_BESCHAEFTIGUNGSGENEHMIGUNG]: async request => {
    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason:
          'Nutzer darf die Beschäftigungsgenehmigung sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für die Beschäftigungsgenehmigung.' };
  },

  /**
   * View the persons calender. Only the user himself or employees with administrative rights have this
   */
  [UserEmployeeDetailsActions.VIEW_KALENDER]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Kalender sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für den Kalender.' };
  },

  /**
   * The user himself, the administrative personal and the the mav can view the recruitment documents
   */
  [UserEmployeeDetailsActions.VIEW_RECRUITMENT_DOCUMENTS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER, Role.PSB, Role.MAV];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Rekrutierungsunterlagen sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für die Rekrutierungsunterlagen.' };
  },

  /**
   * The user himself can view the compliance documents
   */
  [UserEmployeeDetailsActions.VIEW_COMPLIANCE_DOCUMENTS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return { allowed: true, reason: 'Nutzer darf die Compliance-Dokumente sehen. Da er Schulleitung oder PSB ist.' };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für die Compliance-Dokumente.' };
  },

  /**
   * The user himself can view the compliance documents
   */
  [UserEmployeeDetailsActions.VIEW_INTERNAL_EMPLOYMENT_DOCUMENTS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER, Role.PSB];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Internen Beschäftigungsunterlagen sehen. Da er Schulleitung oder PSB ist.',
      };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für die Internen Beschäftigungsunterlagen.' };
  },

  /**
   * As the Führungszeugnis is a delicate document, this is used to elevate the document from others.
   * The timestamp of the document is available for everyone with VIEW_INTERNAL_EMPLOYMENT_DOCUMENTS
   */
  [UserEmployeeDetailsActions.VIEW_FUEHRUNGSZEUGNIS]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the ticket tab
     */
    const allowedRolesOnEmployee = [Role.SCHULLEITER];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return { allowed: true, reason: 'Nutzer darf das Führungszeugnis sehen. Da er Schulleitung ist' };
    }

    return { allowed: false, reason: 'Nutzer hat keine Berechtigung für das Führungszeugnis.' };
  },
};

export const UserEmployeeDetailsPolicy: IPolicy<UserOnUserPayload> = {
  name: 'UserEmployeeDetailsPolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await UserEmployeeDetailsPolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
